/**
 * Pure helpers for the generator address blocks (EPA Form 8700-22 Box 5).
 *
 * The form prescribes leaving the generator's site address blank when it equals
 * the mailing address ("if different than mailing"). These predicates let the
 * review form decide, from the loaded manifest, whether to offer the display-
 * only "Same address" affordance on the site block.
 */

type GeneratorSiteAddressData = {
  generatorLocationStreet1?: string | null;
  generatorLocationStreet2?: string | null;
  generatorLocationCity?: string | null;
  generatorLocationState?: string | null;
  generatorLocationZip?: string | null;
};

type GeneratorMailingAddressData = {
  generatorMailStreet1?: string | null;
  generatorMailStreet2?: string | null;
  generatorMailCity?: string | null;
  generatorMailState?: string | null;
  generatorMailZip?: string | null;
};

// Trim-aware, matching the discrepancy engine's notion of a present value
// (manifestComparisonShared.hasStringValue) — OCR/CSV extraction commonly
// preserves empty cells as "" or whitespace.
function hasValue(value: string | null | undefined): boolean {
  return typeof value === "string" && value.trim() !== "";
}

/** True when the loaded manifest carried no generator site address at all. */
export function isGeneratorSiteAddressBlank(
  wm: GeneratorSiteAddressData | null | undefined
): boolean {
  return (
    !hasValue(wm?.generatorLocationStreet1) &&
    !hasValue(wm?.generatorLocationStreet2) &&
    !hasValue(wm?.generatorLocationCity) &&
    !hasValue(wm?.generatorLocationState) &&
    !hasValue(wm?.generatorLocationZip)
  );
}

/**
 * True when there is any mailing address value to mirror. Permissive on purpose
 * (any field, not just street) so a partial extraction — e.g. city/state/zip
 * present but a missing street — still lets the reviewer mark the site address
 * the same.
 */
export function isGeneratorMailingAddressPresent(
  wm: GeneratorMailingAddressData | null | undefined
): boolean {
  return (
    hasValue(wm?.generatorMailStreet1) ||
    hasValue(wm?.generatorMailStreet2) ||
    hasValue(wm?.generatorMailCity) ||
    hasValue(wm?.generatorMailState) ||
    hasValue(wm?.generatorMailZip)
  );
}
